import React, { useState } from "react";
import PropTypes from 'prop-types';
import renderModal from '@components/show_page/modals/renderModal';
import { get as getLabels } from '@api/v4/resources/labels';
import { get as getSections } from '@api/v4/brandfolders/sections';
import { get as getCollections } from '@api/v4/brandfolders/collections';
import { create as createRule } from '@api/v4/resources/rules';
import RuleSelection from './1_rule_selection';
import TriggerSelection from './2_trigger_selection';
import ActionSelection from './3_action_selection';
import Summary from './4_summary';
import './styles/main';

// import styles here because otherwise they don't work in dev environment
// these imports are not necessary for production environment
import "react-select/dist/react-select.min.css";
import "@components/common/select_react/react_select_styles";

function RuleModal(props) {
  const [stage, updateStage] = useState(1);
  const [ruleType, updateRuleType] = useState();
  const [scope, updateScope] = useState();
  const [actionableKeys, updateActionableKeys] = useState([]);
  const [actionableResources, updateActionableResources] = useState([]);
  const [enforce, updateEnforce] = useState(false);
  const [allSelected, updateAllSelected] = useState(false);

  const resetState = () => {
    updateActionableResources([]);
    updateActionableKeys([]);
    updateScope(null);
    updateStage(1);
    updateEnforce(false);
    updateAllSelected(false);
  };

  const setActionableResources = (type) => {
    if (type.includes("CollectionAssociation")) {
      resetState();
      getCollections().then((resources) => {
        updateActionableResources(resources);
      });
    }
    if (type.includes("LabelAssociation")) {
      resetState();
      getLabels().then((resources) => {
        updateActionableResources(resources);
      });
    }
    if (type.includes("SectionAssociation")) {
      resetState();
      getSections().then((resources) => {
        updateActionableResources(resources);
      });
    }
    resetState();
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      updateActionableKeys([...actionableResources.map((resource) => resource.id)]);
    } else {
      updateActionableKeys([]);
    }
    updateAllSelected(isSelected);
  };

  const submitNewRule = () => {
    createRule({ type: ruleType, actionable_keys: actionableKeys, scope, enforce }).then(() => {
      props.closeModal();
      Notify.create({
        title: t("Rule Enabled"),
        type: "success"
      });
      document.querySelectorAll("a.nav-link[href='rules']")[0].click();
    })
      .catch(({ errors }) => {
        Notify.create({
          title: t("Invalid Rule"),
          body: errors,
          type: "error"
        });
        props.closeModal();
      });
  };

  const renderStage = () => {
    switch (stage) {
      case 2:
        return (
          <TriggerSelection
            ruleType={ruleType}
            updateScope={updateScope}
          />
        );
      case 3:
        return (
          <ActionSelection
            ruleType={ruleType}
            actionableResources={actionableResources}
            actionableKeys={actionableKeys}
            updateActionableKeys={updateActionableKeys}
            allSelected={allSelected}
            handleSelectAll={handleSelectAll}
          />
        );
      case 4:
        return (
          <Summary
            ruleType={ruleType}
            scope={scope}
            actionableResources={actionableResources}
            actionableKeys={actionableKeys}
            enforce={enforce}
            updateEnforce={updateEnforce}
          />
        );
      default:
        return (
          <RuleSelection
            ruleType={ruleType}
            updateRuleType={updateRuleType}
            setActionableResources={setActionableResources}
            resetState={resetState}
          />
        );
    }
  };

  const stageTitle = () => {
    switch (stage) {
      case 2:
        return t("Select Trigger");
      case 3:
        return t("Select Action");
      case 4:
        return t("Summary");
      default:
        return t("Add Rule");
    }
  };

  const disableButton = () => {
    if (stage === 1 && !ruleType) {
      return true;
    }
    if (stage === 2 && !scope) {
      return true;
    }
    if (stage === 3 && !actionableKeys.length) {
      return true;
    }
    return false;
  };

  const handleStageUpdate = () => {
    if (stage < 4) {
      updateStage(stage + 1);
    } else {
      submitNewRule();
    }
  };

  return (
    <div className="modal-content-wrapper add-labels-modal">
      <div className="modal-content-wrapper__header">
        <span className="bff-integrations icon" aria-hidden="true" />
        <h2 className="modal-title">{stageTitle()}</h2>
      </div>
      <div className="modal-content-wrapper__body">
        { renderStage() }
        <div className="button-container">
          <button
            className="button tertiary sm t-close-modal"
            onClick={() => props.closeModal()}
            type="button"
          >
            {t('Cancel')}
          </button>
          <button
            className="button sm primary t-confirm-modal"
            disabled={disableButton()}
            onClick={handleStageUpdate}
            type="button"
          >
            {(stage !== 4) ? t('Next') : t("Create Rule")}
          </button>
        </div>
      </div>
    </div>
  );
}

RuleModal.propTypes = {
  closeModal: PropTypes.func.isRequired
};

const ModalComponent = renderModal(RuleModal, 'RuleModal');
export default ModalComponent;
